'use client';

import { useState } from 'react';
import {
  borderRadiusCss,
  boxShadowCss,
  gradientCss,
  type CssGeneratorVariant,
} from '@/lib/browser-css-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const titles: Record<CssGeneratorVariant, { badge: string; title: string }> = {
  'css-gradient-generator': { badge: 'Gradient', title: 'CSS gradient generator' },
  'css-box-shadow-generator': { badge: 'Shadow', title: 'CSS box-shadow generator' },
  'css-border-radius-generator': { badge: 'Radius', title: 'CSS border-radius generator' },
  'css-triangle-generator': { badge: 'Triangle', title: 'CSS triangle generator' },
  'css-glassmorphism-generator': { badge: 'Glass', title: 'CSS glassmorphism generator' },
  'css-button-generator': { badge: 'Button', title: 'CSS button generator' },
  'css-grid-generator': { badge: 'Grid', title: 'CSS grid generator' },
};

function triangleCss(direction: string, size: number, color: string): string {
  const s = `${size}px solid transparent`;
  const solid = `${size * 1.6}px solid ${color}`;
  if (direction === 'up')
    return `width: 0;\nheight: 0;\nborder-left: ${s};\nborder-right: ${s};\nborder-bottom: ${solid};`;
  if (direction === 'down')
    return `width: 0;\nheight: 0;\nborder-left: ${s};\nborder-right: ${s};\nborder-top: ${solid};`;
  if (direction === 'left')
    return `width: 0;\nheight: 0;\nborder-top: ${s};\nborder-bottom: ${s};\nborder-right: ${solid};`;
  return `width: 0;\nheight: 0;\nborder-top: ${s};\nborder-bottom: ${s};\nborder-left: ${solid};`;
}

export default function BrowserCssGenerator({ variant }: { variant: CssGeneratorVariant }) {
  const meta = titles[variant];
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  // Gradient
  const [gType, setGType] = useState<'linear' | 'radial'>('linear');
  const [angle, setAngle] = useState(90);
  const [color1, setColor1] = useState('#6366f1');
  const [color2, setColor2] = useState('#ec4899');
  // Box shadow
  const [x, setX] = useState(0);
  const [y, setY] = useState(8);
  const [blur, setBlur] = useState(24);
  const [spread, setSpread] = useState(-4);
  const [shadowColor, setShadowColor] = useState('#0f172a');
  const [inset, setInset] = useState(false);
  // Border radius
  const [tl, setTl] = useState(24);
  const [tr, setTr] = useState(24);
  const [br, setBr] = useState(8);
  const [bl, setBl] = useState(8);
  // Triangle
  const [triDir, setTriDir] = useState('up');
  const [triSize, setTriSize] = useState(60);
  const [triColor, setTriColor] = useState('#6366f1');
  // Glassmorphism
  const [glassBlur, setGlassBlur] = useState(8);
  const [glassOpacity, setGlassOpacity] = useState(0.2);
  // Button
  const [btnBg, setBtnBg] = useState('#6366f1');
  const [btnText, setBtnText] = useState('#ffffff');
  const [btnRadius, setBtnRadius] = useState(8);
  const [btnPad, setBtnPad] = useState(12);
  // Grid
  const [gridCols, setGridCols] = useState(3);
  const [gridRows, setGridRows] = useState(2);
  const [gridGap, setGridGap] = useState(12);

  let declaration = '';
  const previewStyle: React.CSSProperties = { width: '100%', height: 160, borderRadius: 12 };

  if (variant === 'css-gradient-generator') {
    const value = gradientCss({ type: gType, angle, color1, color2 });
    declaration = `background: ${value};`;
    previewStyle.background = value;
  } else if (variant === 'css-box-shadow-generator') {
    const value = boxShadowCss({ x, y, blur, spread, color: shadowColor, inset });
    declaration = `box-shadow: ${value};`;
    previewStyle.boxShadow = value;
    previewStyle.background = '#ffffff';
    previewStyle.width = '70%';
    previewStyle.margin = '24px auto';
  } else if (variant === 'css-border-radius-generator') {
    const value = borderRadiusCss({ tl, tr, br, bl });
    declaration = `border-radius: ${value};`;
    previewStyle.borderRadius = value;
    previewStyle.background = 'linear-gradient(135deg, #6366f1, #ec4899)';
  } else if (variant === 'css-triangle-generator') {
    declaration = triangleCss(triDir, triSize, triColor);
    previewStyle.width = 0;
    previewStyle.height = 0;
    previewStyle.borderRadius = 0;
    previewStyle.margin = '24px auto';
    const s = `${triSize}px solid transparent`;
    const solid = `${triSize * 1.6}px solid ${triColor}`;
    if (triDir === 'up') {
      previewStyle.borderLeft = s;
      previewStyle.borderRight = s;
      previewStyle.borderBottom = solid;
    } else if (triDir === 'down') {
      previewStyle.borderLeft = s;
      previewStyle.borderRight = s;
      previewStyle.borderTop = solid;
    } else if (triDir === 'left') {
      previewStyle.borderTop = s;
      previewStyle.borderBottom = s;
      previewStyle.borderRight = solid;
    } else {
      previewStyle.borderTop = s;
      previewStyle.borderBottom = s;
      previewStyle.borderLeft = solid;
    }
  } else if (variant === 'css-glassmorphism-generator') {
    declaration = `background: rgba(255, 255, 255, ${glassOpacity});\nbackdrop-filter: blur(${glassBlur}px);\n-webkit-backdrop-filter: blur(${glassBlur}px);\nborder-radius: 16px;\nborder: 1px solid rgba(255, 255, 255, 0.3);`;
  } else if (variant === 'css-button-generator') {
    declaration = `background: ${btnBg};\ncolor: ${btnText};\npadding: ${btnPad}px ${btnPad * 2}px;\nborder: none;\nborder-radius: ${btnRadius}px;\nfont-weight: 600;\ncursor: pointer;`;
  } else {
    declaration = `display: grid;\ngrid-template-columns: repeat(${gridCols}, 1fr);\ngrid-template-rows: repeat(${gridRows}, 1fr);\ngap: ${gridGap}px;`;
  }

  const isGlass = variant === 'css-glassmorphism-generator';
  const isButton = variant === 'css-button-generator';
  const isGrid = variant === 'css-grid-generator';
  const usesSharedPreview = !isGlass && !isButton && !isGrid;

  async function handleCopy() {
    try {
      await copyText(declaration);
      setCopyMessage('CSS copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">
          Adjust the controls, preview the result live, and copy the CSS. Everything runs in your
          browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {variant === 'css-gradient-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Type</span>
                  <select
                    onChange={(e) => setGType(e.target.value as 'linear' | 'radial')}
                    value={gType}
                  >
                    <option value="linear">Linear</option>
                    <option value="radial">Radial</option>
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>Angle: {angle}deg</span>
                  <input
                    max={360}
                    min={0}
                    onChange={(e) => setAngle(Number(e.target.value))}
                    type="range"
                    value={angle}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Color 1</span>
                  <input onChange={(e) => setColor1(e.target.value)} type="color" value={color1} />
                </label>
                <label className="tool-live-field">
                  <span>Color 2</span>
                  <input onChange={(e) => setColor2(e.target.value)} type="color" value={color2} />
                </label>
              </>
            ) : null}
            {variant === 'css-box-shadow-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Offset X: {x}px</span>
                  <input
                    max={50}
                    min={-50}
                    onChange={(e) => setX(Number(e.target.value))}
                    type="range"
                    value={x}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Offset Y: {y}px</span>
                  <input
                    max={50}
                    min={-50}
                    onChange={(e) => setY(Number(e.target.value))}
                    type="range"
                    value={y}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Blur: {blur}px</span>
                  <input
                    max={100}
                    min={0}
                    onChange={(e) => setBlur(Number(e.target.value))}
                    type="range"
                    value={blur}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Spread: {spread}px</span>
                  <input
                    max={50}
                    min={-50}
                    onChange={(e) => setSpread(Number(e.target.value))}
                    type="range"
                    value={spread}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Color</span>
                  <input
                    onChange={(e) => setShadowColor(e.target.value)}
                    type="color"
                    value={shadowColor}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Inset</span>
                  <input
                    checked={inset}
                    onChange={(e) => setInset(e.target.checked)}
                    type="checkbox"
                  />
                </label>
              </>
            ) : null}
            {variant === 'css-border-radius-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Top-left: {tl}px</span>
                  <input
                    max={100}
                    min={0}
                    onChange={(e) => setTl(Number(e.target.value))}
                    type="range"
                    value={tl}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Top-right: {tr}px</span>
                  <input
                    max={100}
                    min={0}
                    onChange={(e) => setTr(Number(e.target.value))}
                    type="range"
                    value={tr}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Bottom-right: {br}px</span>
                  <input
                    max={100}
                    min={0}
                    onChange={(e) => setBr(Number(e.target.value))}
                    type="range"
                    value={br}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Bottom-left: {bl}px</span>
                  <input
                    max={100}
                    min={0}
                    onChange={(e) => setBl(Number(e.target.value))}
                    type="range"
                    value={bl}
                  />
                </label>
              </>
            ) : null}
            {variant === 'css-triangle-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Direction</span>
                  <select onChange={(e) => setTriDir(e.target.value)} value={triDir}>
                    <option value="up">Up</option>
                    <option value="down">Down</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>Size: {triSize}px</span>
                  <input
                    max={120}
                    min={10}
                    onChange={(e) => setTriSize(Number(e.target.value))}
                    type="range"
                    value={triSize}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Color</span>
                  <input
                    onChange={(e) => setTriColor(e.target.value)}
                    type="color"
                    value={triColor}
                  />
                </label>
              </>
            ) : null}
            {variant === 'css-glassmorphism-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Blur: {glassBlur}px</span>
                  <input
                    max={30}
                    min={0}
                    onChange={(e) => setGlassBlur(Number(e.target.value))}
                    type="range"
                    value={glassBlur}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Transparency: {Math.round(glassOpacity * 100)}%</span>
                  <input
                    max={0.6}
                    min={0.05}
                    onChange={(e) => setGlassOpacity(Number(e.target.value))}
                    step={0.05}
                    type="range"
                    value={glassOpacity}
                  />
                </label>
              </>
            ) : null}
            {isButton ? (
              <>
                <label className="tool-live-field">
                  <span>Background</span>
                  <input onChange={(e) => setBtnBg(e.target.value)} type="color" value={btnBg} />
                </label>
                <label className="tool-live-field">
                  <span>Text color</span>
                  <input
                    onChange={(e) => setBtnText(e.target.value)}
                    type="color"
                    value={btnText}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Radius: {btnRadius}px</span>
                  <input
                    max={40}
                    min={0}
                    onChange={(e) => setBtnRadius(Number(e.target.value))}
                    type="range"
                    value={btnRadius}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Padding: {btnPad}px</span>
                  <input
                    max={30}
                    min={4}
                    onChange={(e) => setBtnPad(Number(e.target.value))}
                    type="range"
                    value={btnPad}
                  />
                </label>
              </>
            ) : null}
            {isGrid ? (
              <>
                <label className="tool-live-field">
                  <span>Columns: {gridCols}</span>
                  <input
                    max={6}
                    min={1}
                    onChange={(e) => setGridCols(Number(e.target.value))}
                    type="range"
                    value={gridCols}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Rows: {gridRows}</span>
                  <input
                    max={6}
                    min={1}
                    onChange={(e) => setGridRows(Number(e.target.value))}
                    type="range"
                    value={gridRows}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Gap: {gridGap}px</span>
                  <input
                    max={40}
                    min={0}
                    onChange={(e) => setGridGap(Number(e.target.value))}
                    type="range"
                    value={gridGap}
                  />
                </label>
              </>
            ) : null}
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy CSS
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              background: isGlass
                ? 'linear-gradient(135deg, #f97316, #ec4899, #6366f1)'
                : undefined,
            }}
          >
            {isGlass ? (
              <div
                style={{
                  width: '70%',
                  height: 120,
                  borderRadius: 16,
                  border: '1px solid rgba(255,255,255,0.3)',
                  background: `rgba(255, 255, 255, ${glassOpacity})`,
                  backdropFilter: `blur(${glassBlur}px)`,
                  WebkitBackdropFilter: `blur(${glassBlur}px)`,
                }}
              />
            ) : isButton ? (
              <button
                style={{
                  background: btnBg,
                  color: btnText,
                  padding: `${btnPad}px ${btnPad * 2}px`,
                  border: 'none',
                  borderRadius: btnRadius,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                type="button"
              >
                Button
              </button>
            ) : isGrid ? (
              <div
                style={{
                  display: 'grid',
                  width: '100%',
                  gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                  gridTemplateRows: `repeat(${gridRows}, 1fr)`,
                  gap: gridGap,
                }}
              >
                {Array.from({ length: gridCols * gridRows }, (_, i) => (
                  <div key={i} style={{ background: '#6366f1', borderRadius: 6, height: 40 }} />
                ))}
              </div>
            ) : (
              <div style={previewStyle} />
            )}
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>CSS</span>
            <textarea
              readOnly
              rows={usesSharedPreview && variant !== 'css-triangle-generator' ? 2 : 6}
              value={declaration}
            />
          </label>
          <p className="tool-live-panel-note">
            Live preview and CSS output, generated in your browser.
          </p>
        </section>
      </div>
    </div>
  );
}
